import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { RequisitiService } from '../../services/requisiti.service';
import { ClienteModel } from '../../core/models/api/cliente.model';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Subscription } from 'rxjs';
import { UserRoleService } from '../../services/user-role.service';
import { SearchShareService } from '../../services/search-share.service';

@Component({
  selector: 'app-clienti',
  standalone: false,
  templateUrl: './clienti.component.html',
  styleUrl: './clienti.component.scss'
})
export class ClientiComponent implements OnInit, OnDestroy {
  clienti: ClienteModel[] = [];
  filteredClienti: ClienteModel[] = [];
  loading = false;
  searchText = '';
  
  // Dialog creazione cliente
  showCreateClienteDialog = false;

  // Inline editing
  editingClienteId: number | null = null;
  editingRagioneSociale: string = '';
  savingEdit = false;

  private clientiSub?: Subscription;

  constructor(
    private requisitiService: RequisitiService,
    private logger: NGXLogger,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private userRoleService: UserRoleService,
    private router: Router,
    private searchShareService: SearchShareService
  ) {}

  /**
   * Verifica se l'utente può modificare (non è VIEWER)
   */
  canModify(): boolean {
    return this.userRoleService.canModify();
  }

  /**
   * Verifica se l'utente è VIEWER
   */
  isViewer(): boolean {
    return this.userRoleService.isViewer();
  }

  ngOnInit(): void {
    this.loadClienti();
  }

  ngOnDestroy(): void {
    if (this.clientiSub) {
      this.clientiSub.unsubscribe();
    }
  }

  /**
   * Carica i clienti dal backend
   */
  private loadClienti(): void {
    this.loading = true;
    this.clientiSub = this.requisitiService.getClienti().subscribe({
      next: clienti => {
        this.clienti = clienti;
        this.applyFilter();
        this.loading = false;
        this.logger.info('[CLIENTI] Clienti caricati:', clienti.length);
      },
      error: err => {
        this.logger.error('[CLIENTI] Errore nel caricamento dei clienti:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante il caricamento dei clienti',
          life: 5000
        });
        this.loading = false;
      }
    });
  }

  /**
   * Applica il filtro di ricerca
   */
  applyFilter(): void {
    const term = this.searchText.toLowerCase().trim();
    
    if (!term) {
      this.filteredClienti = [...this.clienti];
    } else {
      this.filteredClienti = this.clienti.filter(cliente =>
        cliente.ragioneSociale.toLowerCase().includes(term) ||
        cliente.id.toString().includes(term)
      );
    }
  }

  /**
   * Gestisce il cambio del testo di ricerca
   */
  onSearchChange(): void {
    this.applyFilter();
  }

  /**
   * Pulisce il filtro di ricerca
   */
  clearSearch(): void {
    this.searchText = '';
    this.applyFilter();
  }

  /**
   * Apre il dialog per creare un nuovo cliente
   */
  openCreateClienteDialog(): void {
    this.showCreateClienteDialog = true;
  }

  /**
   * Callback chiamato quando un cliente è stato creato
   */
  onClienteCreated(): void {
    this.logger.info('[CLIENTI] Nuovo cliente creato, ricarico la lista');
    this.loadClienti();
  }

  /**
   * Avvia la modifica inline di un cliente
   */
  startEdit(cliente: ClienteModel): void {
    if (!this.canModify()) return;
    
    this.editingClienteId = cliente.id;
    this.editingRagioneSociale = cliente.ragioneSociale;
  }

  /**
   * Annulla la modifica inline
   */
  cancelEdit(): void {
    this.editingClienteId = null;
    this.editingRagioneSociale = '';
    this.savingEdit = false;
  }

  /**
   * Verifica se la ragione sociale modificata è duplicata
   */
  isEditDuplicate(): boolean {
    if (!this.editingRagioneSociale.trim()) return false;

    const ragioneSocialeNormalized = this.editingRagioneSociale.trim().toLowerCase();
    return this.clienti.some(
      c => c.id !== this.editingClienteId && 
           c.ragioneSociale.toLowerCase() === ragioneSocialeNormalized
    );
  }

  /**
   * Salva la modifica della ragione sociale
   */
  async saveEdit(): Promise<void> {
    if (!this.editingClienteId || !this.editingRagioneSociale.trim()) {
      return;
    }

    if (this.isEditDuplicate()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore Validazione',
        detail: 'Esiste già un cliente con questa ragione sociale',
        life: 5000
      });
      return;
    }

    // Verifica se il valore è cambiato
    const clienteOriginale = this.clienti.find(c => c.id === this.editingClienteId);
    if (clienteOriginale && clienteOriginale.ragioneSociale === this.editingRagioneSociale.trim()) {
      this.cancelEdit();
      return;
    }

    this.savingEdit = true;

    try {
      await new Promise<ClienteModel>((resolve, reject) => {
        this.requisitiService
          .updateCliente(this.editingClienteId!, this.editingRagioneSociale.trim())
          .subscribe({
            next: (clienteAggiornato) => resolve(clienteAggiornato),
            error: (error) => reject(error)
          });
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Cliente aggiornato con successo',
        life: 3000
      });

      this.cancelEdit();
      this.loadClienti();
    } catch (error) {
      this.logger.error('[CLIENTI] Errore nell\'aggiornamento del cliente:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile aggiornare il cliente. Riprova più tardi.',
        life: 5000
      });
      this.savingEdit = false;
    }
  }

  /**
   * Conferma ed elimina un cliente
   */
  confirmDelete(cliente: ClienteModel): void {
    if (!this.canModify()) return;

    this.confirmationService.confirm({
      message: `Sei sicuro di voler eliminare il cliente <strong>"${cliente.ragioneSociale}"</strong>?<br><br>Questa operazione non può essere annullata.`,
      header: 'Conferma Eliminazione',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Elimina',
      rejectLabel: 'Annulla',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => {
        this.deleteCliente(cliente.id);
      }
    });
  }

  /**
   * Elimina un cliente
   */
  private async deleteCliente(clienteId: number): Promise<void> {
    try {
      await new Promise<void>((resolve, reject) => {
        this.requisitiService.deleteCliente(clienteId).subscribe({
          next: () => resolve(),
          error: (error) => reject(error)
        });
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: 'Cliente eliminato con successo',
        life: 3000
      });

      this.loadClienti();
    } catch (error) {
      this.logger.error('[CLIENTI] Errore nell\'eliminazione del cliente:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Impossibile eliminare il cliente. Riprova più tardi.',
        life: 5000
      });
    }
  }

  /**
   * Verifica se un cliente è in modalità editing
   */
  isEditing(clienteId: number): boolean {
    return this.editingClienteId === clienteId;
  }

  /**
   * Naviga alla home con il filtro impostato sul cliente selezionato
   */
  navigateToHomeWithCliente(cliente: ClienteModel): void {
    // Non navigare se non ci sono referenze
    if (!cliente.numeroReferenze || cliente.numeroReferenze === 0) {
      return;
    }

    // Crea i parametri di ricerca con il cliente selezionato
    // IMPORTANTE: usa la ragione sociale (campo cliente nelle referenze)
    const searchParams = {
      selectedClienti: [cliente.ragioneSociale]
    };

    // Genera la query string
    const queryString = this.searchShareService.serializeSearchParams(searchParams);

    // Costruisce i query params dall'URL generata
    const urlParams = new URLSearchParams(queryString);
    const queryParams: any = {};
    urlParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    // Naviga alla home con i parametri
    this.router.navigate(['/'], { queryParams });

    this.logger.debug('[CLIENTI] Navigazione alla home con cliente:', {
      ragioneSociale: cliente.ragioneSociale,
      queryParams
    });
  }
}
